import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import { 
  downloadCurriculumContent, 
  formatFileSize,
  estimateDownloadTime,
  type DownloadProgress 
} from '../utils/curriculumDownload';
import './CurriculumBrowser.css';

// Types
interface GradeOption {
  grade: number;
  label: string;
  label_fa: string;
  courseCount: number;
  contentCount: number;
}

interface CourseOption {
  id: string;
  course: string;
  subject: string;
  label: string;
  label_fa: string;
  topicCount: number;
  contentCount: number;
  hasVideo: boolean;
  hasAudio: boolean;
  hasPdf: boolean;
}

interface TopicOption {
  topicNumber: number;
  label: string;
  label_fa: string;
  title: string | null;
  title_fa: string | null;
  contentCount: number;
  hasVideo: boolean;
  hasAudio: boolean;
  hasPdf: boolean;
  isSummary?: boolean;
}

interface ContentItem {
  id: string;
  title: string;
  titleFa: string | null;
  description: string | null;
  descriptionFa: string | null;
  contentType: string;
  r2AudioPath: string | null;
  r2SlidesPath: string | null;
  r2PackagePath: string | null;
  durationSeconds: number | null;
  fileSizeBytes: number | null;
  sortOrder: number;
  grade: number;
  course: string;
  topicNumber: number;
  schoolName: string;
  schoolNameFa: string | null;
}

export function CurriculumBrowser() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isLoading, accessToken } = useAuth();
  
  // State
  const [grades, setGrades] = useState<GradeOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [content, setContent] = useState<ContentItem[]>([]);
  
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);
  
  const [loadingGrades, setLoadingGrades] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Download state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadItems, setDownloadItems] = useState<ContentItem[]>([]);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [estimatedSize, setEstimatedSize] = useState<number>(0);

  const isRTL = i18n.language === 'fa';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Fetch grades on mount
  const fetchGrades = useCallback(async () => {
    if (!accessToken) return;
    
    setLoadingGrades(true);
    setError(null);
    
    try {
      const response = await fetch('/api/curriculum/grades', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch grades');
      }

      setGrades(data.data?.grades || []);
    } catch (err) {
      console.error('Error fetching grades:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch grades');
    } finally {
      setLoadingGrades(false);
    }
  }, [accessToken]);

  // Fetch courses when grade is selected
  const fetchCourses = useCallback(async (grade: number) => {
    if (!accessToken) return;
    
    setLoadingCourses(true);
    setCourses([]);
    setTopics([]);
    setContent([]);
    setSelectedCourse(null);
    setSelectedTopic(null);
    
    try {
      const response = await fetch(`/api/curriculum/grades/${grade}/courses`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch courses');
      }

      setCourses(data.data?.courses || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch courses');
    } finally {
      setLoadingCourses(false);
    }
  }, [accessToken]);

  // Fetch topics when course is selected
  const fetchTopics = useCallback(async (courseId: string) => {
    if (!accessToken) return;
    
    setLoadingTopics(true);
    setTopics([]);
    setContent([]);
    setSelectedTopic(null);
    
    try {
      const response = await fetch(`/api/curriculum/courses/${courseId}/topics`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch topics');
      }

      setTopics(data.data?.topics || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch topics');
    } finally {
      setLoadingTopics(false);
    }
  }, [accessToken]);

  // Fetch content when topic is selected
  const fetchContent = useCallback(async (courseId: string, topicNumber: number) => {
    if (!accessToken) return;
    
    setLoadingContent(true);
    setContent([]);
    
    try {
      const topicId = `${courseId}-${topicNumber}`;
      const response = await fetch(`/api/curriculum/topics/${topicId}/content`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch content');
      }

      setContent(data.data?.content || []);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
    } finally {
      setLoadingContent(false);
    }
  }, [accessToken]);

  // Initial load
  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  // Handle grade selection
  const handleGradeSelect = (grade: number) => {
    setSelectedGrade(grade);
    fetchCourses(grade);
  };

  // Handle course selection
  const handleCourseSelect = (courseId: string) => {
    setSelectedCourse(courseId);
    fetchTopics(courseId);
  };

  // Handle topic selection
  const handleTopicSelect = (topicNumber: number) => {
    if (!selectedCourse) return;
    setSelectedTopic(topicNumber);
    fetchContent(selectedCourse, topicNumber);
  };

  // Handle play content
  const handlePlay = (contentId: string) => {
    navigate(`/player/${contentId}`);
  };

  // Handle download content - single item
  const handleDownload = async (item: ContentItem) => {
    setDownloadItems([item]);
    setEstimatedSize(item.fileSizeBytes || 0);
    setDownloadError(null);
    setDownloadProgress(null);
    setShowDownloadModal(true);
  };

  // Handle download topic - all content in topic
  const handleDownloadTopic = async () => {
    if (content.length === 0) return;
    
    const totalSize = content.reduce((sum, item) => sum + (item.fileSizeBytes || 0), 0);
    setDownloadItems(content);
    setEstimatedSize(totalSize);
    setDownloadError(null);
    setDownloadProgress(null);
    setShowDownloadModal(true);
  };

  // Execute download
  const executeDownload = async () => {
    if (!accessToken || downloadItems.length === 0) return;
    
    setIsDownloading(true);
    setDownloadError(null);
    
    const contentIds = downloadItems.map(item => item.id);
    
    const result = await downloadCurriculumContent(
      contentIds,
      'development', // Use development mode for now
      accessToken,
      undefined, // No device ID for development mode
      (progress) => setDownloadProgress(progress)
    );
    
    setIsDownloading(false);
    
    if (!result.success) {
      setDownloadError(result.error || 'Download failed');
    } else {
      // Close modal on success
      setShowDownloadModal(false);
      setDownloadItems([]);
      setDownloadProgress(null);
    }
  };

  // Close download modal
  const closeDownloadModal = () => {
    if (!isDownloading) {
      setShowDownloadModal(false);
      setDownloadItems([]);
      setDownloadProgress(null);
      setDownloadError(null);
    }
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size - local version
  const formatFileSizeLocal = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get content type icon
  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return '🎬';
      case 'audio': return '🎧';
      case 'pdf': return '📄';
      default: return '📁';
    }
  };

  // Handle redirects
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      }
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className={`curriculum-browser ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cb-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (!user || user.role === 'teacher') {
    return (
      <div className={`curriculum-browser ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="cb-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={`curriculum-browser ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="cb-header">
        <div className="cb-header-content">
          <a href="/" className="cb-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </a>
          <div className="cb-header-actions">
            <LanguageSwitcher />
            <button onClick={handleLogout} className="cb-logout-btn">
              {t('common.logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="cb-layout">
        {/* Sidebar Navigation */}
        <aside className="cb-sidebar">
          <div className="cb-user-info">
            <div className="cb-user-avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
            </div>
            <div className="cb-user-details">
              <span className="cb-user-name">{user.name || user.email}</span>
              <span className="cb-user-role">{t('auth.student')}</span>
            </div>
          </div>

          <nav className="cb-nav">
            <button
              className="cb-nav-item"
              onClick={() => navigate('/student')}
            >
              <span className="cb-nav-icon">📚</span>
              <span className="cb-nav-label">{t('curriculum.myLibrary')}</span>
            </button>
            <button
              className="cb-nav-item active"
              onClick={() => {}}
            >
              <span className="cb-nav-icon">🎓</span>
              <span className="cb-nav-label">{t('curriculum.title')}</span>
            </button>
            <button
              className="cb-nav-item"
              onClick={() => navigate('/student')}
            >
              <span className="cb-nav-icon">🏫</span>
              <span className="cb-nav-label">{t('schools.title')}</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="cb-main">
          <div className="cb-content">
            {/* Breadcrumb */}
            <div className="cb-breadcrumb">
              <span 
                className={`cb-breadcrumb-item ${!selectedGrade ? 'active' : 'clickable'}`}
                onClick={() => {
                  setSelectedGrade(null);
                  setSelectedCourse(null);
                  setSelectedTopic(null);
                  setCourses([]);
                  setTopics([]);
                  setContent([]);
                }}
              >
                {t('curriculum.title')}
              </span>
              {selectedGrade && (
                <>
                  <span className="cb-breadcrumb-separator">/</span>
                  <span 
                    className={`cb-breadcrumb-item ${!selectedCourse ? 'active' : 'clickable'}`}
                    onClick={() => {
                      setSelectedCourse(null);
                      setSelectedTopic(null);
                      setTopics([]);
                      setContent([]);
                    }}
                  >
                    {isRTL ? `صنف ${selectedGrade}` : `Grade ${selectedGrade}`}
                  </span>
                </>
              )}
              {selectedCourse && (
                <>
                  <span className="cb-breadcrumb-separator">/</span>
                  <span 
                    className={`cb-breadcrumb-item ${!selectedTopic ? 'active' : 'clickable'}`}
                    onClick={() => {
                      setSelectedTopic(null);
                      setContent([]);
                    }}
                  >
                    {courses.find(c => c.id === selectedCourse)?.[isRTL ? 'label_fa' : 'label'] || selectedCourse}
                  </span>
                </>
              )}
              {selectedTopic !== null && (
                <>
                  <span className="cb-breadcrumb-separator">/</span>
                  <span className="cb-breadcrumb-item active">
                    {selectedTopic === 0 
                      ? (isRTL ? 'خلاصه درس' : 'Course Summary')
                      : (isRTL ? `موضوع ${selectedTopic}` : `Topic ${selectedTopic}`)
                    }
                  </span>
                </>
              )}
            </div>

            {error && (
              <div className="cb-error-message">{error}</div>
            )}

            {/* Grade Selection */}
            {!selectedGrade && (
              <div className="cb-section">
                <h2 className="cb-section-title">{t('curriculum.selectGrade')}</h2>
                {loadingGrades ? (
                  <div className="cb-loading-inline">{t('common.loading')}</div>
                ) : grades.length === 0 ? (
                  <div className="cb-empty-state">
                    <div className="cb-empty-icon">📚</div>
                    <h3>{t('curriculum.noContent')}</h3>
                    <p>{t('curriculum.subscribeFirst')}</p>
                    <button 
                      className="cb-action-btn primary"
                      onClick={() => navigate('/student')}
                    >
                      {t('curriculum.browseSchools')}
                    </button>
                  </div>
                ) : (
                  <div className="cb-grade-grid">
                    {grades.map(grade => (
                      <button
                        key={grade.grade}
                        className="cb-grade-card"
                        onClick={() => handleGradeSelect(grade.grade)}
                      >
                        <span className="cb-grade-number">{grade.grade}</span>
                        <span className="cb-grade-label">
                          {isRTL ? grade.label_fa : grade.label}
                        </span>
                        <span className="cb-grade-meta">
                          {grade.courseCount} {t('curriculum.courses')} • {grade.contentCount} {t('curriculum.items')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Course Selection */}
            {selectedGrade && !selectedCourse && (
              <div className="cb-section">
                <h2 className="cb-section-title">{t('curriculum.selectCourse')}</h2>
                {loadingCourses ? (
                  <div className="cb-loading-inline">{t('common.loading')}</div>
                ) : courses.length === 0 ? (
                  <div className="cb-empty-state">
                    <div className="cb-empty-icon">📖</div>
                    <h3>{t('curriculum.noCourses')}</h3>
                    <p>{t('curriculum.noCoursesDesc')}</p>
                  </div>
                ) : (
                  <div className="cb-course-grid">
                    {courses.map(course => (
                      <button
                        key={course.id}
                        className="cb-course-card"
                        onClick={() => handleCourseSelect(course.id)}
                      >
                        <div className="cb-course-header">
                          <h3>{isRTL ? course.label_fa : course.label}</h3>
                          <div className="cb-course-icons">
                            {course.hasVideo && <span title={t('curriculum.video')}>🎬</span>}
                            {course.hasAudio && <span title={t('curriculum.audio')}>🎧</span>}
                            {course.hasPdf && <span title={t('curriculum.pdf')}>📄</span>}
                          </div>
                        </div>
                        <div className="cb-course-meta">
                          {course.topicCount} {t('curriculum.topics')} • {course.contentCount} {t('curriculum.items')}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Topic Selection */}
            {selectedCourse && selectedTopic === null && (
              <div className="cb-section">
                <h2 className="cb-section-title">{t('curriculum.selectTopic')}</h2>
                {loadingTopics ? (
                  <div className="cb-loading-inline">{t('common.loading')}</div>
                ) : topics.length === 0 ? (
                  <div className="cb-empty-state">
                    <div className="cb-empty-icon">📝</div>
                    <h3>{t('curriculum.noTopics')}</h3>
                    <p>{t('curriculum.noTopicsDesc')}</p>
                  </div>
                ) : (
                  <div className="cb-topic-list">
                    {topics.map(topic => (
                      <button
                        key={topic.topicNumber}
                        className={`cb-topic-card ${topic.contentCount === 0 ? 'coming-soon' : ''} ${topic.isSummary ? 'summary' : ''}`}
                        onClick={() => topic.contentCount > 0 && handleTopicSelect(topic.topicNumber)}
                        disabled={topic.contentCount === 0}
                      >
                        <div className="cb-topic-number">
                          {topic.isSummary ? '📋' : topic.topicNumber}
                        </div>
                        <div className="cb-topic-info">
                          <h4>
                            {isRTL 
                              ? (topic.title_fa || topic.label_fa)
                              : (topic.title || topic.label)
                            }
                          </h4>
                          {topic.contentCount > 0 ? (
                            <div className="cb-topic-meta">
                              <div className="cb-topic-icons">
                                {topic.hasVideo && <span title={t('curriculum.video')}>🎬</span>}
                                {topic.hasAudio && <span title={t('curriculum.audio')}>🎧</span>}
                                {topic.hasPdf && <span title={t('curriculum.pdf')}>📄</span>}
                              </div>
                              <span>{topic.contentCount} {t('curriculum.items')}</span>
                            </div>
                          ) : (
                            <span className="cb-coming-soon-badge">{t('curriculum.comingSoon')}</span>
                          )}
                        </div>
                        <div className="cb-topic-arrow">→</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Content List */}
            {selectedTopic !== null && (
              <div className="cb-section">
                <h2 className="cb-section-title">{t('curriculum.content')}</h2>
                {loadingContent ? (
                  <div className="cb-loading-inline">{t('common.loading')}</div>
                ) : content.length === 0 ? (
                  <div className="cb-empty-state">
                    <div className="cb-empty-icon">📭</div>
                    <h3>{t('curriculum.noContent')}</h3>
                    <p>{t('curriculum.noContentDesc')}</p>
                  </div>
                ) : (
                  <div className="cb-content-list">
                    {content.map(item => (
                      <div key={item.id} className="cb-content-card">
                        <div className="cb-content-icon">
                          {getContentTypeIcon(item.contentType)}
                        </div>
                        <div className="cb-content-info">
                          <h4>{isRTL ? (item.titleFa || item.title) : item.title}</h4>
                          {(item.description || item.descriptionFa) && (
                            <p className="cb-content-desc">
                              {isRTL ? (item.descriptionFa || item.description) : item.description}
                            </p>
                          )}
                          <div className="cb-content-meta">
                            <span className="cb-content-type">
                              {t(`curriculum.${item.contentType}`)}
                            </span>
                            {item.durationSeconds && (
                              <span>⏱️ {formatDuration(item.durationSeconds)}</span>
                            )}
                            {item.fileSizeBytes && (
                              <span>📦 {formatFileSizeLocal(item.fileSizeBytes)}</span>
                            )}
                          </div>
                        </div>
                        <div className="cb-content-actions">
                          <button 
                            className="cb-action-btn primary"
                            onClick={() => handlePlay(item.id)}
                          >
                            ▶️ {t('content.play')}
                          </button>
                          <button 
                            className="cb-action-btn secondary"
                            onClick={() => handleDownload(item)}
                          >
                            📥 {t('content.download')}
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Download All Button */}
                    {content.length > 1 && (
                      <div className="cb-download-all">
                        <button 
                          className="cb-action-btn primary cb-download-all-btn"
                          onClick={handleDownloadTopic}
                        >
                          📥 {isRTL ? `دانلود همه (${content.length} فایل)` : `Download All (${content.length} files)`}
                          <span className="cb-download-size">
                            {formatFileSizeLocal(content.reduce((sum, item) => sum + (item.fileSizeBytes || 0), 0))}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="cb-modal-overlay" onClick={closeDownloadModal}>
          <div className="cb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cb-modal-header">
              <h3>{isRTL ? 'دانلود محتوا' : 'Download Content'}</h3>
              {!isDownloading && (
                <button className="cb-modal-close" onClick={closeDownloadModal}>×</button>
              )}
            </div>
            
            <div className="cb-modal-body">
              {!isDownloading && !downloadProgress && (
                <>
                  <div className="cb-download-info">
                    <div className="cb-download-stat">
                      <span className="cb-download-stat-label">
                        {isRTL ? 'تعداد فایل‌ها:' : 'Files:'}
                      </span>
                      <span className="cb-download-stat-value">{downloadItems.length}</span>
                    </div>
                    <div className="cb-download-stat">
                      <span className="cb-download-stat-label">
                        {isRTL ? 'حجم تقریبی:' : 'Estimated Size:'}
                      </span>
                      <span className="cb-download-stat-value">{formatFileSize(estimatedSize)}</span>
                    </div>
                    <div className="cb-download-stat">
                      <span className="cb-download-stat-label">
                        {isRTL ? 'زمان تقریبی:' : 'Estimated Time:'}
                      </span>
                      <span className="cb-download-stat-value">{estimateDownloadTime(estimatedSize)}</span>
                    </div>
                  </div>
                  
                  <div className="cb-download-items">
                    {downloadItems.map(item => (
                      <div key={item.id} className="cb-download-item">
                        <span className="cb-download-item-icon">{getContentTypeIcon(item.contentType)}</span>
                        <span className="cb-download-item-title">
                          {isRTL ? (item.titleFa || item.title) : item.title}
                        </span>
                        <span className="cb-download-item-size">
                          {formatFileSizeLocal(item.fileSizeBytes)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {(isDownloading || downloadProgress) && (
                <div className="cb-download-progress">
                  <div className="cb-progress-phase">
                    {downloadProgress?.phase === 'preparing' && (isRTL ? 'آماده‌سازی...' : 'Preparing...')}
                    {downloadProgress?.phase === 'downloading' && (isRTL ? 'در حال دانلود...' : 'Downloading...')}
                    {downloadProgress?.phase === 'packaging' && (isRTL ? 'ایجاد بسته...' : 'Creating package...')}
                  </div>
                  
                  {downloadProgress?.currentFile && (
                    <div className="cb-progress-file">
                      {downloadProgress.currentFile}
                    </div>
                  )}
                  
                  <div className="cb-progress-bar-container">
                    <div 
                      className="cb-progress-bar" 
                      style={{ width: `${downloadProgress?.percentComplete || 0}%` }}
                    />
                  </div>
                  
                  <div className="cb-progress-stats">
                    <span>{downloadProgress?.currentIndex || 0} / {downloadProgress?.totalFiles || 0}</span>
                    <span>{downloadProgress?.percentComplete || 0}%</span>
                  </div>
                </div>
              )}
              
              {downloadError && (
                <div className="cb-download-error">
                  {downloadError}
                </div>
              )}
            </div>
            
            <div className="cb-modal-footer">
              {!isDownloading && (
                <>
                  <button 
                    className="cb-action-btn secondary"
                    onClick={closeDownloadModal}
                  >
                    {isRTL ? 'انصراف' : 'Cancel'}
                  </button>
                  <button 
                    className="cb-action-btn primary"
                    onClick={executeDownload}
                  >
                    📥 {isRTL ? 'شروع دانلود' : 'Start Download'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
